import { useSearchParams } from "react-router";
import {
  Alert,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  SearchType,
  useGetAutoRecipeDefaultsQuery,
} from "store/api/generatedApi";
import { searchTypeLabels } from "components/autoRecipeParameters/searchTypeLabels";
import {
  AutoRecipeDefaultsRecovery,
  getAutoRecipeRecoveryContext,
} from "./AutoRecipeDefaultsRecovery";
import { AutoRecipeDefaultsEditor } from "./AutoRecipeDefaultsEditor";
import { useDefaultParameterVisit } from "./useDefaultParameterVisit";

export const AutoRecipeDefaultParameters = ({
  setConfirmOpen,
  isActive,
}: {
  isActive: boolean;
  setConfirmOpen: (open: boolean) => void;
}) => {
  const { auto_search_types: searchTypes } = useTenantData();
  const [searchParams] = useSearchParams();
  const [selection, setSelection] = useState<SearchType | null>(
    () =>
      searchTypes.find((type) => type === searchParams.get("searchType")) ??
      null
  );
  const [hasChanges, setHasChanges] = useState(false);
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const searchType =
    selection !== null && searchTypes.includes(selection)
      ? selection
      : searchTypes[0];
  const defaults = useGetAutoRecipeDefaultsQuery(
    { tenantName, searchType: searchType ?? "charging" },
    { skip: !searchType, refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );
  useDefaultParameterVisit({
    tenantName,
    page: "search_parameters",
    isActive,
  });
  if (!searchType)
    return <Alert severity="info">{t("autoNoSearchTypes")}</Alert>;
  const recoveryContext = getAutoRecipeRecoveryContext({
    error: defaults.error,
    searchType,
  });
  return (
    <Stack gap={3} sx={{ overflow: "auto", pt: 2, pb: 2 }}>
      <TextField
        select
        label={t("searchType")}
        value={searchType}
        disabled={hasChanges}
        helperText={hasChanges ? t("autoSaveBeforeSwitching") : undefined}
        onChange={(event) => {
          setSelection(event.target.value as SearchType);
        }}
      >
        {searchTypes.map((option) => (
          <MenuItem key={option} value={option}>
            {t(searchTypeLabels[option])}
          </MenuItem>
        ))}
      </TextField>
      {defaults.isError && !recoveryContext ? (
        <Alert severity="error">{t("errorLoadingData")}</Alert>
      ) : null}
      {recoveryContext ? (
        <AutoRecipeDefaultsRecovery
          context={recoveryContext}
          refresh={defaults.refetch}
          isFetching={defaults.isFetching}
        />
      ) : defaults.currentData ? (
        <AutoRecipeDefaultsEditor
          key={`${tenantName}/${searchType}`}
          defaults={defaults.currentData}
          isActive={isActive}
          refresh={defaults.refetch}
          setConfirmOpen={setConfirmOpen}
          setHasChanges={setHasChanges}
        />
      ) : defaults.isFetching ? (
        <CircularProgress />
      ) : null}
    </Stack>
  );
};
